import React, { useEffect, useState } from "react";
import axios from "../api/baseurl";
import { toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { useNavigate } from "react-router-dom";
import AdminDashboard from "./admindashboard";
import CancelAppointment from "./cancelAppointment";
import { format, parse, startOfMonth } from 'date-fns';
import { Pagination } from "react-bootstrap";
import "./hm-theme.css";

const formatDateForDisplay = (date) => format(date, "dd/MMM/yyyy hh:mm:ss a");

function Financials() {
  const [loading, setLoading] = useState(false);
  const [userPayments, setUserPayments] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(0);
  const [totalPages, setTotalPages] = useState(0);
  const [filter, setFilter] = useState("ALL");
  const [fromDate, setFromDate] = useState(formatDateForDisplay(startOfMonth(new Date())));
  const [toDate, setToDate] = useState(formatDateForDisplay(new Date()));
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [isCancelPopupOpen, setIsCancelPopupOpen] = useState(false);

  const navigate = useNavigate();

  const searchData = async (pageNumber = 0) => {
    setLoading(true);
    try {
      const token = JSON.parse(localStorage.getItem("data")).token;
      const requestData = {
        searchTerm,
        fromDate,
        toDate,
        status: filter === "ALL" ? null : filter,
      };
      const response = await axios.post(
        `/api/user/userpayment/searchPayments`, requestData,
        {
          params: { page: pageNumber },
          headers: { Authorization: `Bearer ${token}` },
        });
      setUserPayments(response.data.content || []);
      setCurrentPage(response.data.number || 0);
      setTotalPages(response.data.totalPages || 0);
    } catch (error) {
      handleError(error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    setCurrentPage(0);
  }, [searchTerm, filter, fromDate, toDate]);

  useEffect(() => {
    searchData(currentPage);
  }, [searchTerm, filter, currentPage, fromDate, toDate]);

  const handleCancelClick = (payment) => {
    setSelectedPayment(payment);
    setIsCancelPopupOpen(true);
  };

  const handleCloseCancelPopup = () => {
    setIsCancelPopupOpen(false);
    searchData(currentPage);
  };

  const handlePageChange = (pageNumber) => {
    setCurrentPage(pageNumber);
  };

  const renderPageNumbers = () => {
    const pageNumbers = [];
    const maxPageNumbersToShow = 5;
    let startPage = Math.max(0, currentPage - 2);
    let endPage = Math.min(totalPages - 1, currentPage + 2);

    if (currentPage <= 2) {
      endPage = Math.min(totalPages - 1, maxPageNumbersToShow - 1);
    }

    if (currentPage >= totalPages - 3) {
      startPage = Math.max(0, totalPages - maxPageNumbersToShow);
    }

    for (let i = startPage; i <= endPage; i++) {
      pageNumbers.push(
        <Pagination.Item key={i} active={i === currentPage} onClick={() => handlePageChange(i)}>
          {i + 1}
        </Pagination.Item>
      );
    }
    return pageNumbers;
  };

  function handleError(error) {
    if (error.response?.status === 401) {
      navigate(`/signin`);
      toast.error("Your session has expired. Please login.");
    } else {
      toast.error(error.response?.data?.message || error.message || "An error occurred.");
    }
  }

  const getStatusBadge = (status) => {
    if (!status) return "hm-badge hm-badge-info";
    const s = status.toUpperCase();
    if (s.includes("SUCCESS") || s.includes("PROCESSED")) return "hm-badge hm-badge-success";
    if (s.includes("REFUND") || s.includes("FAIL") || s.includes("CANCEL")) return "hm-badge hm-badge-danger";
    if (s.includes("PENDING")) return "hm-badge hm-badge-warning";
    return "hm-badge hm-badge-info";
  };

  return (
    <div>
      <AdminDashboard />
      {loading && <div className="loading"></div>}

      <div className="col-12 mx-xl-auto ps-xl-10 mt-10 pe-xl-4">
        <div className="hm-page-wrapper">

          {/* Page Header */}
          <div className="hm-page-header mb-4">
            <div>
              <h2 className="hm-page-title">
                <i className="material-icons">account_balance_wallet</i>
                Financials & Payments
              </h2>
              <p className="hm-page-subtitle">Track payments, filter transactions, and manage refunds</p>
            </div>
          </div>

          {/* Filter Card */}
          <div className="hm-card mb-4">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">filter_alt</i>
                Search & Filters
              </h3>
            </div>
            <div className="hm-card-body">
              <div className="row g-3 align-items-end">
                <div className="col-md-4">
                  <label className="form-label small fw-semibold">Search</label>
                  <div className="position-relative">
                    <input
                      type="search"
                      className="form-control ps-4"
                      placeholder="Search company or user name..."
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                    />
                    <i className="material-icons position-absolute text-muted" style={{ left: "10px", top: "50%", transform: "translateY(-50%)", fontSize: "1.1rem" }}>
                      search
                    </i>
                  </div>
                </div>

                <div className="col-md-3">
                  <label className="form-label small fw-semibold">Status</label>
                  <select
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                    className="form-select"
                  >
                    <option value="ALL">All Statuses</option>
                    <option value="SUCCESS">Payment Success</option>
                    <option value="PAYMENT PROCESSED">Payment Processed</option>
                    <option value="REFUND PROCESSED">Refund Processed</option>
                  </select>
                </div>

                <div className="col-md-2">
                  <label className="form-label small fw-semibold">From Date</label>
                  <input
                    type="date"
                    value={fromDate ? format(parse(fromDate, 'dd/MMM/yyyy hh:mm:ss a', new Date()), 'yyyy-MM-dd') : ''}
                    onChange={(e) => setFromDate(formatDateForDisplay(parse(e.target.value, 'yyyy-MM-dd', new Date())))}
                    className="form-control"
                  />
                </div>

                <div className="col-md-3">
                  <label className="form-label small fw-semibold">To Date</label>
                  <input
                    type="date"
                    value={toDate ? format(parse(toDate, 'dd/MMM/yyyy hh:mm:ss a', new Date()), 'yyyy-MM-dd') : ''}
                    onChange={(e) => setToDate(formatDateForDisplay(parse(e.target.value, 'yyyy-MM-dd', new Date())))}
                    className="form-control"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Payment Table Card */}
          <div className="hm-card">
            <div className="hm-card-header">
              <h3 className="hm-card-title">
                <i className="material-icons">receipt_long</i>
                Transactions
              </h3>
              <span className="hm-badge hm-badge-primary">{userPayments.length} records</span>
            </div>
            <div className="hm-card-body p-0">
              <div style={{ overflowX: "auto" }}>
                <table className="hm-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Company</th>
                      <th>Employee</th>
                      <th>Date</th>
                      <th>Payment ID</th>
                      <th>Amount</th>
                      <th>Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {userPayments.length === 0 ? (
                      <tr>
                        <td colSpan="7">
                          <div className="hm-empty-state">
                            <i className="material-icons">payments</i>
                            <p>{loading ? "Loading transactions..." : "No payment records found"}</p>
                          </div>
                        </td>
                      </tr>
                    ) : (
                      userPayments.map((payment, index) => (
                        <tr key={index}>
                          <td>{currentPage * 10 + index + 1}</td>
                          <td className="fw-semibold">{payment.company?.companyname || "—"}</td>
                          <td>
                            <div className="fw-semibold">{payment.employee?.name} {payment.employee?.lastname}</div>
                            {payment.memberName && (
                              <div className="text-muted" style={{ fontSize: "0.75rem" }}>{payment.memberName}</div>
                            )}
                          </td>
                          <td>{payment.paymentdate || "—"}</td>
                          <td><code style={{ fontSize: "0.78rem" }}>{payment.razorpay_payment_id || "—"}</code></td>
                          <td className="fw-bold">₹ {payment.paymentAmount || 0}</td>
                          <td>
                            <span className={getStatusBadge(payment.paymentStatus)}>
                              {payment.paymentStatus}
                            </span>
                            {payment.paymentStatus === "PAYMENT PROCESSED" && payment.appointmentStatus !== "APPOINTMENT COMPLETED" && (
                              <div className="mt-2">
                                <button
                                  className="btn btn-outline-danger btn-sm rounded-pill px-3"
                                  onClick={() => handleCancelClick(payment)}
                                >
                                  Refund
                                </button>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center py-3">
                  <Pagination className="hm-pagination">
                    <Pagination.First onClick={() => handlePageChange(0)} disabled={currentPage === 0} />
                    <Pagination.Prev onClick={() => handlePageChange(currentPage - 1)} disabled={currentPage === 0} />
                    {renderPageNumbers()}
                    <Pagination.Next onClick={() => handlePageChange(currentPage + 1)} disabled={currentPage >= totalPages - 1} />
                    <Pagination.Last onClick={() => handlePageChange(totalPages - 1)} disabled={currentPage >= totalPages - 1} />
                  </Pagination>
                </div>
              )}
            </div>
          </div>

        </div>
      </div>

      {isCancelPopupOpen && (
        <CancelAppointment
          isOpen={isCancelPopupOpen}
          onRequestClose={handleCloseCancelPopup}
          payment={selectedPayment}
        />
      )}
    </div>
  );
}

export default Financials;